import { useEffect, useMemo, useState } from "react"
import { AccordionEventKey } from "react-bootstrap/esm/AccordionContext"

import classNames from "classnames"

import { useEvent } from "shared/lib/useEvent"
import usePrevious from "shared/lib/usePrevious"
import { ProductConfiguratorOptionPayload } from "shared/types/ProductConfigurator"
import { TabType } from "shared/types/Tab"

import { AddonAccordionProps } from "../AddonAccordion"
import { AddonAccordionContent } from "../AddonAccordionContent"

export function useAddonsAccordionViewModel({
  addonOptions,
  isDisabled,
  isLoading,
  onDelete,
  onChange
}: AddonAccordionProps) {
  const previousAddonOptions = usePrevious(addonOptions)

  const selectedAddons = useMemo(
    () =>
      addonOptions
        ?.filter(option => option.payload.isSelected)
        .map(option => option.key),
    [addonOptions]
  )

  const [activeKey, setActiveKey] = useState<AccordionEventKey>(
    addonOptions?.find(addon => addon.payload.isConfigurable)?.key || null
  )

  useEffect(() => {
    previousAddonOptions?.forEach(previous => {
      const currentAddon = addonOptions?.find(
        current => previous.key === current.key
      )
      if (currentAddon?.payload.isSelected && !previous.payload.isSelected) {
        setActiveKey(currentAddon.key)
      }
    })
  }, [previousAddonOptions, addonOptions])

  const handleSelect = useEvent((key: string) => {
    const isSelected = selectedAddons?.includes(key)
    setActiveKey(key)
    if (typeof key === "string") {
      if (isSelected) {
        onDelete?.(key)
      } else {
        onChange?.({ [key]: { value: true } }, key)
      }
    }
  })

  const tabs: TabType<ProductConfiguratorOptionPayload>[] = useMemo(
    () =>
      (addonOptions
        ?.map(addon => {
          const tab: TabType<ProductConfiguratorOptionPayload> = {
            ...addon,
            className: classNames({
              selected: addon.payload.isSelected
            }),
            content: addon.payload.isSelected ? (
              <AddonAccordionContent
                addonKey={addon.key}
                schema={addon.payload.schema}
                formData={addon.payload.formData}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isConfigurable={addon.payload.isConfigurable}
                onFormDataChange={onChange}
              />
            ) : null
          }
          return tab
        })
        .filter(Boolean) as TabType<ProductConfiguratorOptionPayload>[]) || [],
    [addonOptions, isDisabled]
  )

  return {
    activeKey,
    onSelect: handleSelect,
    onExpand: setActiveKey,
    tabs,
    selectedTabs: selectedAddons,
    isDisabled
  }
}
